/**
 * RepositoryEmptyStates Component
 * Reusable empty state components for repository panels
 */

import React from 'react';
import { Database, FolderOpen, CheckCircle2, AlertCircle, AlertTriangle, RefreshCw, Link2, Plus, LoaderCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

export type RepositoryDomainContext = 'flowchart' | 'orgchart' | 'genogram';

type DomainVocabulary = {
  singular: string;
  plural: string;
};

const DOMAIN_VOCABULARY: Record<RepositoryDomainContext, DomainVocabulary> = {
  flowchart: { singular: 'process', plural: 'processes' },
  orgchart: { singular: 'record', plural: 'records' },
  genogram: { singular: 'person', plural: 'people' },
};

const getDomainVocabulary = (domain: RepositoryDomainContext): DomainVocabulary =>
  DOMAIN_VOCABULARY[domain];

function domainCountLabelNoun(
  domain: RepositoryDomainContext,
  count: number,
  translate: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (domain === 'orgchart') {
    return String(translate('assistant.repositoryRecordNoun', { count }));
  }
  if (domain === 'genogram') {
    return String(translate('assistant.repositoryPersonNoun', { count }));
  }
  return String(translate('assistant.repositoryProcessNoun', { count }));
}

export function domainItemsLabel(
  domain: RepositoryDomainContext,
  translate: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (domain === 'orgchart') {
    return translate('assistant.repositoryItemsRecords', { defaultValue: 'records' }) as string;
  }
  if (domain === 'genogram') {
    return translate('assistant.repositoryItemsPeople', { defaultValue: 'people' }) as string;
  }
  return translate('assistant.repositoryItemsProcesses', { defaultValue: 'processes' }) as string;
}

interface BaseEmptyStateProps {
  className?: string;
  domain?: RepositoryDomainContext;
}

interface NoRepositoryConnectedProps extends BaseEmptyStateProps {
  onConnect?: () => void;
}

export const NoRepositoryConnected: React.FC<NoRepositoryConnectedProps> = ({
  onConnect,
  className,
  domain = 'flowchart',
}) => {
  const { t } = useTranslation('diagram');
  const items = domainItemsLabel(domain, t);

  return (
    <div className={cn('flex flex-col items-center justify-center p-6 text-center', className)}>
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <Database className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-medium text-foreground mb-1">
        {t('assistant.repositoryEmptyNoSourceTitle', { defaultValue: 'No Data Source Connected' })}
      </h3>
      <p className="text-xs text-muted-foreground mb-4 max-w-[200px]">
        {t('assistant.repositoryEmptyNoSourceDescription', {
          items,
          defaultValue: `Connect an external data source to sync ${items}.`,
        })}
      </p>
      {onConnect && (
        <Button variant="outline" size="sm" onClick={onConnect}>
          <Link2 className="h-4 w-4 mr-2" />
          {t('assistant.connectDataSourceDialogTitle', { defaultValue: 'Connect Data Source' })}
        </Button>
      )}
    </div>
  );
};

interface RepositoryEmptyProps extends BaseEmptyStateProps {
  sourceName?: string;
  onRefresh?: () => void;
  /**
   * Optional "Add source" CTA. When provided, the empty state renders
   * a primary button that invokes this callback — typically opens the
   * unified <ImportSourceModal>. See
   * plans/archive/compass/import-surfaces-consolidation-plan-archived.md §B6.
   */
  onAddSource?: () => void;
  /** Label for the Add source button. */
  addSourceLabel?: string;
}

export const RepositoryEmpty: React.FC<RepositoryEmptyProps> = ({
  sourceName,
  onRefresh,
  onAddSource,
  addSourceLabel = 'Add source',
  className,
  domain = 'flowchart',
}) => {
  const { t } = useTranslation('diagram');
  const items = domainItemsLabel(domain, t);

  return (
    <div className={cn('flex flex-col items-center justify-center p-6 text-center', className)}>
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <FolderOpen className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-medium text-foreground mb-1">
        {t('assistant.repositoryEmptyTitle', { defaultValue: 'Data Source is Empty' })}
      </h3>
      <p className="text-xs text-muted-foreground mb-4 max-w-[220px]">
        {sourceName
          ? t('assistant.repositoryEmptyHintWithSource', {
              items,
              sourceName,
              defaultValue: `Add ${items} to "${sourceName}" to see them here`,
            })
          : t('assistant.repositoryEmptyHintNoSource', {
              items,
              defaultValue: `Add ${items} to your external data source to see them here`,
            })}
      </p>
      <div className="flex items-center gap-2">
        {onAddSource && (
          <Button variant="default" size="sm" onClick={onAddSource}>
            <Plus className="h-4 w-4 mr-1.5" />
            {addSourceLabel}
          </Button>
        )}
        {onRefresh && (
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('assistant.repositoryEmptyRefresh', { defaultValue: 'Refresh' })}
          </Button>
        )}
      </div>
    </div>
  );
};

interface AllItemsOnCanvasProps extends BaseEmptyStateProps {
  itemCount: number;
  uiType?: string;
  onRefresh?: () => void;
}

export const AllItemsOnCanvas: React.FC<AllItemsOnCanvasProps> = ({
  itemCount,
  onRefresh,
  className,
  uiType = 'full',
  domain = 'flowchart',
}) => {
  const { t } = useTranslation('diagram');
  const label = domainCountLabelNoun(domain, itemCount, t);

  return uiType === 'full'
    ?  (
        <div className={cn('flex flex-col items-center justify-center p-6 text-center', className)}>
          <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-sm font-medium text-foreground mb-1">
            {t('assistant.repositoryAllItemsOnCanvasTitle', { defaultValue: 'All Items on Canvas' })}
          </h3>
          <p className="text-xs text-muted-foreground mb-4 max-w-[200px]">
            {t('assistant.repositoryAllItemsOnCanvasDescription', {
              count: itemCount,
              label,
              defaultValue: `All ${itemCount} ${label} from the data source are already on the canvas`,
            })}
          </p>
          {onRefresh && (
            <Button variant="ghost" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('assistant.repositoryCheckForUpdates', { defaultValue: 'Check for Updates' })}
            </Button>
          )}
        </div>
      )
    : (
        <div className={cn('flex items-center gap-2 justify-between text-center px-4 py-1', className)}>
          <p className="text-xs text-muted-foreground max-w-[200px]">
            {t('assistant.repositoryAllOnCanvasBanner', {
              count: itemCount,
              label,
              defaultValue: `All ${itemCount} ${label} on the canvas`,
            })}
          </p>
          <CheckCircle2 className="h-6 w-6 text-green-600" />
          {onRefresh && (
            <Button variant="ghost" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('assistant.repositoryCheckForUpdates', { defaultValue: 'Check for Updates' })}
            </Button>
          )}
        </div>
      )
};

interface NoSearchResultsProps extends BaseEmptyStateProps {
  searchText: string;
  onClearSearch?: () => void;
}

export const NoSearchResults: React.FC<NoSearchResultsProps> = ({
  searchText,
  onClearSearch,
  className,
  domain = 'flowchart',
}) => {
  const { t } = useTranslation('diagram');
  const items = domainItemsLabel(domain, t);

  return (
    <div className={cn('flex flex-col items-center justify-center p-6 text-center', className)}>
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <AlertCircle className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-medium text-foreground mb-1">
        {t('assistant.repositoryNoSearchTitle', { defaultValue: 'No Results Found' })}
      </h3>
      <p className="text-xs text-muted-foreground mb-4 max-w-[200px]">
        {t('assistant.repositoryNoSearchHint', {
          items,
          searchText,
          defaultValue: `No ${items} match "${searchText}"`,
        })}
      </p>
      {onClearSearch && (
        <Button variant="ghost" size="sm" onClick={onClearSearch}>
          {t('assistant.repositoryClearSearchButton', { defaultValue: 'Clear Search' })}
        </Button>
      )}
    </div>
  );
};

interface RepositoryErrorProps extends BaseEmptyStateProps {
  error?: string;
  onRetry?: () => void;
  onConnectAnother?: () => void;
}

export const RepositoryError: React.FC<RepositoryErrorProps> = ({
  error,
  onRetry,
  onConnectAnother,
  className,
  domain = 'flowchart',
}) => {
  const { t } = useTranslation('diagram');
  const { plural } = getDomainVocabulary(domain);
  const itemsLabel = domainItemsLabel(domain, t);
  const fetchRecordsEn = 'Failed to fetch records';
  const errorMessage =
    error === fetchRecordsEn
      ? (t('assistant.repositoryErrorFetchRecords', { defaultValue: fetchRecordsEn }) as string)
      : error ??
        (t('assistant.repositoryErrorFetchHint', {
          items: itemsLabel,
          defaultValue: `Failed to fetch ${plural}. Retry or connect another.`,
        }) as string);

  return (
    <div className={cn('flex flex-col items-center justify-center p-1 text-center font-inter', className)}>
      <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <AlertTriangle className="h-6 w-6 text-destructive" />
      </div>
      <h3 className="text-sm font-normal text-destructive mb-1">
        {t('assistant.repositoryErrorTitle', { defaultValue: 'Failed to Load Data Source' })}
      </h3>
      <p className="text-[10px] text-muted-foreground mb-4 font-normal">
        {errorMessage}
      </p>
      <div className="flex items-center mt-6 justify-center gap-6">
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="cursor-pointer">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('assistant.repositoryRetry', { defaultValue: 'Retry' })}
          </Button>
        )}

        {onConnectAnother && (
          <button
            type="button"
            onClick={onConnectAnother}
            className="inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:text-sky-800 cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            <span>{t('assistant.repositoryConnectAnother', { defaultValue: 'Connect Another' })}</span>
          </button>
        )}
      </div>
    </div>
  );
};

interface RepositoryLoadingProps extends BaseEmptyStateProps {
  message?: string;
}

export const RepositoryLoading: React.FC<RepositoryLoadingProps> = ({
  message,
  className,
  domain = 'flowchart',
}) => {
  const { t } = useTranslation('diagram');
  const { plural } = getDomainVocabulary(domain);
  const itemsLabel = domainItemsLabel(domain, t);
  const loadingText =
    message ??
    (t('assistant.repositoryLoadingItems', {
      items: itemsLabel,
      defaultValue: `Loading ${plural}...`,
    }) as string);

  return (
    <div className={cn('flex flex-col items-center justify-center p-6 text-center', className)}>
      {/* <RefreshCw className="h-8 w-8 text-muted-foreground animate-spin mb-4" /> */}
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#DBEAFE] mb-2">
        <LoaderCircle className="h-7 w-7 text-[#3982C2] animate-spin" />
      </div>
      <p className="text-sm text-muted-foreground">{loadingText}</p>
    </div>
  );
};
